import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Button,
  Timeline,
  Alert,
  Typography,
  Space,
  Spin,
} from 'antd';
import { apiService } from '../../services/api';
import {
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  HeartOutlined,
  PlusOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const { Title, Text } = Typography;

// Mock data for charts
const revenueData = [
  { month: 'Jan', revenue: 45000, clients: 120 },
  { month: 'Feb', revenue: 52000, clients: 135 },
  { month: 'Mar', revenue: 48000, clients: 128 },
  { month: 'Apr', revenue: 61000, clients: 152 },
  { month: 'May', revenue: 55000, clients: 145 },
  { month: 'Jun', revenue: 67000, clients: 168 },
];

const coachWorkloadData = [
  { name: 'Sarah Johnson', clients: 25, utilization: 85 },
  { name: 'Mike Chen', clients: 22, utilization: 75 },
  { name: 'Emily Davis', clients: 28, utilization: 95 },
  { name: 'Alex Rodriguez', clients: 20, utilization: 68 },
];

const serviceTypeData = [
  { name: 'Training Only', value: 45, color: '#1890ff' },
  { name: 'Nutrition Only', value: 25, color: '#52c41a' },
  { name: 'Training + Nutrition', value: 28, color: '#fa8c16' },
  { name: 'Full Package', value: 2, color: '#722ed1' },
];

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [backendHealth, setBackendHealth] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Fetching backend health...');
        
        // Test backend connection
        const healthResponse = await apiService.healthCheck();
        console.log('✅ Backend health response:', healthResponse);
        setBackendHealth(healthResponse);

        // Fetch user profile
        try {
          const profileResponse = await apiService.getProfile();
          console.log('👤 User profile response:', profileResponse);
          setStats(profileResponse);
        } catch (profileError) {
          console.warn('⚠️ User profile not available:', profileError);
        }

        // Fetch user stats
        try {
          const statsResponse = await apiService.getUserStats();
          console.log('📊 User stats response:', statsResponse);
          setStats(statsResponse);
        } catch (statsError) {
          console.warn('⚠️ User stats not available:', statsError);
        }

      } catch (err: any) {
        console.error('❌ Dashboard API error:', err);
        setError(err.message || 'Failed to connect to backend');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading dashboard...</div>
      </div>
    );
  }

  // Mock recent activities
  const recentActivities = [
    {
      title: 'New client registration',
      description: 'John Smith signed up for Premium Training package',
      time: '2 minutes ago',
      type: 'success',
    },
    {
      title: 'Payment received',
      description: 'Sarah Johnson - Monthly subscription payment processed',
      time: '15 minutes ago',
      type: 'success',
    },
    {
      title: 'Program updated',
      description: 'Mike Chen updated training program for 3 clients',
      time: '1 hour ago',
      type: 'info',
    },
    {
      title: 'Coach assigned',
      description: 'Emily Davis assigned to new client Lisa Brown',
      time: '2 hours ago',
      type: 'info',
    },
  ];

  // Mock alerts
  const alerts = [
    {
      type: 'warning' as const,
      message: '5 subscriptions expiring in the next 7 days',
      action: 'View Details',
    },
    {
      type: 'error' as const,
      message: '3 overdue tasks require attention',
      action: 'Review Tasks',
    },
    {
      type: 'info' as const,
      message: 'Monthly revenue report is ready',
      action: 'Download Report',
    },
  ];

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Dashboard</Title>
        <Text type="secondary">
          Welcome back! Here's what's happening with your fitness business today.
        </Text>
      </div>

      {/* Backend Status & Alerts Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {/* Backend Connection Status */}
            {error ? (
              <Alert
                type="error"
                message="Backend Connection Failed"
                description={`Cannot connect to backend: ${error}`}
                showIcon
                action={
                  <Button size="small" onClick={() => window.location.reload()}>
                    Retry
                  </Button>
                }
              />
            ) : backendHealth ? (
              <Alert
                type="success"
                message="✅ Backend Connected Successfully"
                description={`Server is running. Environment: ${backendHealth.data?.environment || 'unknown'}`}
                showIcon
                closable
              />
            ) : null}
            
            {/* Original alerts */}
            {alerts.map((alert, index) => (
              <Alert
                key={index}
                type={alert.type}
                message={alert.message}
                action={
                  <Button size="small" type="text">
                    {alert.action}
                  </Button>
                }
                showIcon
                closable
              />
            ))}
          </Space>
        </Col>
      </Row>

      {/* Key Metrics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Active Clients"
              value={168}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix={
                <Text type="success" style={{ fontSize: 12 }}>
                  +12% this month
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Coaches"
              value={8}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  2 online now
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Monthly Revenue"
              value={67000}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#fa8c16' }}
              suffix={
                <Text type="success" style={{ fontSize: 12 }}>
                  +18% from last month
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Client Satisfaction"
              value={4.8}
              prefix={<HeartOutlined />}
              precision={1}
              valueStyle={{ color: '#f5222d' }}
              suffix={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  / 5.0 stars
                </Text>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card title="Quick Actions" style={{ marginBottom: 24 }}>
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} size="large">
            Add New Client
          </Button>
          <Button icon={<TeamOutlined />} size="large">
            Add New Coach
          </Button>
          <Button icon={<CheckCircleOutlined />} size="large">
            Create Program
          </Button>
          <Button icon={<CalendarOutlined />} size="large">
            View Today's Tasks
          </Button>
        </Space>
      </Card>

      {/* Charts and Analytics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Revenue Trend */}
        <Col xs={24} lg={16}>
          <Card title="Revenue & Client Growth (Last 6 Months)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Bar yAxisId="left" dataKey="revenue" fill="#1890ff" name="Revenue ($)" />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="clients"
                  stroke="#52c41a"
                  strokeWidth={3}
                  name="Active Clients"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Service Type Distribution */}
        <Col xs={24} lg={8}>
          <Card title="Service Type Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={serviceTypeData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                >
                  {serviceTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Bottom Row */}
      <Row gutter={[16, 16]}>
        {/* Coach Workload */}
        <Col xs={24} lg={12}>
          <Card title="Coach Workload Distribution">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={coachWorkloadData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="clients" fill="#1890ff" name="Clients" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Recent Activity */}
        <Col xs={24} lg={12}>
          <Card title="Recent Activity" extra={<Button type="text">View All</Button>}>
            <Timeline>
              {recentActivities.map((activity, index) => (
                <Timeline.Item
                  key={index}
                  color={activity.type === 'success' ? 'green' : 'blue'}
                >
                  <div>
                    <Text strong>{activity.title}</Text>
                    <br />
                    <Text type="secondary">{activity.description}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {activity.time}
                    </Text>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard; 